import { memo, useMemo } from 'react';
import IconHighlight from '../../IconHighlight.jsx';
import Avatar from '../../Avatar.jsx';

function HighlightMatch({ text, query }) {
  const parts = useMemo(() => {
    if (!query || !text) return null;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const split = text.split(new RegExp(`(${escaped})`, 'gi'));
    return split.length > 1 ? split : null;
  }, [text, query]);

  if (!parts) return text;
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={i}
            className="rounded bg-violet-100 not-italic text-violet-900 dark:bg-violet-900/40 dark:text-violet-200"
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {import('lucide-react').LucideIcon} [props.icon]
 * @param {string} [props.iconBg]
 * @param {string} [props.iconColor]
 * @param {string} [props.initials]
 * @param {boolean} [props.isHighlighted]
 * @param {string} [props.query] - Active search query for text highlight
 * @param {() => void} [props.onSelect]
 */
const TopbarGlobalSearchResultRow = memo(function TopbarGlobalSearchResultRow({
  title,
  subtitle,
  icon: Icon,
  iconBg = 'Primary',
  iconColor = 'PrimaryAlt',
  initials,
  isHighlighted = false,
  query = '',
  onSelect,
  onSelectFallback,
  data,
}) {
  const handleClick = onSelect ?? (() => onSelectFallback?.(data));
  const hasInitials = typeof initials === 'string' && initials.trim().length > 0 && !Icon;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[
        'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
        isHighlighted
          ? 'bg-violet-100/90 dark:bg-violet-900/30'
          : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {hasInitials ? (
        <Avatar
          size="sm"
          initials={initials}
          alt={title}
          className="!h-7 !w-7 !min-h-7 !min-w-7 bg-violet-600 text-[10px] font-semibold text-white"
        />
      ) : Icon ? (
        <IconHighlight
          icon={Icon}
          bgColor={iconBg}
          iconColor={iconColor}
          size="small"
          className="!h-7 !w-7 !min-h-7 !min-w-7 !rounded-md !p-1.5 [&_svg]:!h-4 [&_svg]:!w-4"
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-5 text-[#2A2A2A] dark:text-gray-100">
          <HighlightMatch text={title} query={query} />
        </p>
        {subtitle ? (
          <p className="truncate text-xs font-medium leading-5 text-[#5C5C5C] dark:text-gray-500">
            {subtitle}
          </p>
        ) : null}
      </div>
    </button>
  );
});

export default TopbarGlobalSearchResultRow;
